import { getField, updateField } from 'vuex-map-fields';
import { updateOpportunityOrderInfo } from '@/api';
import { getECPayToken, createECPayTransaction } from '@/api/ECPay';
import { BANK_CODE_TO_NAME } from '@/configs/bankcodeToName.js';
import donationWayType from '@/configs/donationWay';
import donationPeriodType from '@/configs/donationPeriod';

import { oneline } from '@/helpers/common';
import { getGATracking } from '@/helpers/ga';
import { getMetaTracking } from '@/helpers/meta';
import moment from 'moment-timezone';

export default {
  namespaced: true,

  state: {
    oppoId: '', // 機會 ID
    regularOrderId: '', // 定期定額編號
    orderType: '', // 付款方式 ['CREDIT'|'ATM'|'CVS'|'LINE']
    ecpayUrl: '',
    ecpayToken: '', // 付款 token
    ecpayInfo: {
      oppoECPayNo: '', // 綠界交易編號
      amount: '', // 付款金額
      paymentPeriod: '', // 付款週期 （單筆捐款 / 定期定額）
      paymentType: '', // 付款類型
      paymentAccount: '', // 付款帳號銀行代號
      paymentNo: '', // 付款超商代碼
      expiredTime: '', // 繳費期限
      oppoUpdateOrNot: '', // 是否要更新機會 (0、1）
      tradeStatus: '', // 成功狀態碼 (0、1）
      tradeStatusMessage: '', // 成功狀態訊息: 尚未付款(0)、付款成功(1)
      processDate: '', // 信用卡交易時間
    },
  },

  getters: {
    getField,
    ecpayInfo(state, getters, rootState) {
      const fields = [];

      fields.push({
        name: '捐款類型',
        value: state.ecpayInfo.paymentPeriod,
      });

      fields.push({
        name: '捐款金額',
        value: 'NT$' + state.ecpayInfo.amount,
      });

      fields.push({
        name: '捐款編號',
        value: state.oppoId,
      });

      if(rootState.donation.donationWay === donationWayType.ECPAY_SINGLE_ATM) {
        fields.push({
          name: '付款銀行帳號',
          value: state.ecpayInfo.paymentAccount,
        });

        fields.push({
          name: '繳費期限',
          value: state.ecpayInfo.expiredTime,
        });

      } else if(rootState.donation.donationWay === donationWayType.ECPAY_SINGLE_CVS) {
        fields.push({
          name: '付款超商代碼',
          value: state.ecpayInfo.paymentNo,
        });

        fields.push({
          name: '繳費期限',
          value: state.ecpayInfo.expiredTime,
        });
      } else if(rootState.donation.donationWay === donationWayType.ECPAY_SINGLE_CREDIT) {

        fields.push({
          name: '捐款結果',
          value: state.ecpayInfo.tradeStatusMessage,
        });

        fields.push({
          name: '付款時間',
          value: state.ecpayInfo.processDate,
        });

      } else if(rootState.donation.donationWay === donationWayType.ECPAY_PERIOD_CREDIT) {
        fields.push({
          name: '捐款結果',
          value: state.ecpayInfo.tradeStatusMessage,
        });
      }

      return fields;
    },
  },

  mutations: {
    updateField,

    updateSingleOrderId(state, order_id) {
      state.oppoId = order_id;
    },

    updateRegularOrderId(state, order_id) {
      state.regularOrderId = order_id;
    },

    updateEcpayToken(state, result) {
      state.ecpayToken = result.data.ECPayData.Token;
    },

    update_ATM_CSV_Result(state, payload) {
      state.oppoId = payload['OrderInfo']['MerchantTradeNo'];
      state.ecpayInfo.paymentType = payload['OrderInfo']['PaymentType'];
      state.ecpayInfo.amount = payload['OrderInfo']['TradeAmt'] ? payload['OrderInfo']['TradeAmt'] : '';
      state.ecpayInfo.oppoECPayNo = payload['OrderInfo']['TradeNo'];
      state.ecpayInfo.tradeStatus = payload['OrderInfo']['TradeStatus'];

      if(payload['OrderInfo']['PaymentType'] == 'CVS') { // 超商代碼
        state.ecpayInfo.expiredTime = payload['CVSInfo']['ExpireDate'];
        state.ecpayInfo.paymentNo = payload['CVSInfo']['PaymentNo'];
      } else if (payload['OrderInfo']['PaymentType'] == 'ATM') { // ATM
        const BankCode = payload['ATMInfo']['BankCode'];
        const BankName = BANK_CODE_TO_NAME[BankCode];
        state.ecpayInfo.expiredTime = payload['ATMInfo']['ExpireDate'];
        state.ecpayInfo.paymentAccount = `(${ BankCode + ' ' + BankName }) ${ payload['ATMInfo']['vAccount'] }`;
      }
    },
    updateCreditCardResult(state, payload) {
      state.oppoId = payload.oppoID;
      state.ecpayInfo.amount = payload.amount ? 'NT$' + payload.amount : '';
      state.ecpayInfo.paymentPeriod = payload.paymentPeriod;
      state.ecpayInfo.tradeStatus = payload.tradeStatus;
      state.ecpayInfo.tradeStatusMessage = payload.tradeStatusMessage;
      state.ecpayInfo.oppoECPayNo = payload.tradeNo;
    },
  },

  actions: {
    async getECPayToken({ state, rootState, rootGetters, commit }) {

      const transactionNo = rootState.donation.donationPeriod == donationPeriodType.DONATION_PERIOD ? state.regularOrderId : state.oppoId;

      const payload = {
        transactionNo: transactionNo,
        transactionType: rootGetters['donation/orderType'],
        transactionAmount: rootState.donation.amount,
        email: rootState.donation.contact.email,
      };

      try{
        const result = await getECPayToken(payload);
        commit('updateEcpayToken', result);
        return result;
      } catch(e) {
        throw new Error(e + ' payload: ' + JSON.stringify(payload));
      }
    },

    async createTransaction({ state, rootState }, payToken) {
      const transactionNo = rootState.donation.donationPeriod == donationPeriodType.DONATION_PERIOD ? state.regularOrderId : state.oppoId;
      const payload = {
        transactionNo: transactionNo,
        payToken: payToken,
        tracking: {
          ...getGATracking(), // 讓後端 GA4 MP 事件歸到同一位使用者，避免重複交易
          ...getMetaTracking(), // fbc/fbp：讓後端 CAPI 事件能歸因到廣告點擊（提高 fbc 涵蓋率）
          email: rootState.donation.contact.email, // 後端會 SHA-256 雜湊後供 Meta 進階配對
          phone: rootState.donation.contact.phone,
        },
      };
      try{
        const result = await createECPayTransaction(payload);
        if(result['rtnStatus'] == 'Fail') {
          throw new Error(result.msg + ' payload: ' + JSON.stringify(payload));
        }
        return result;
      } catch(e) {
        throw new Error(e);
      }
    },

    async updateResultToSaleforce({ state, rootGetters }) {
      // 將給使用者看到的訊息儲存至 Saleforce

      // 為了避免使用者 refresh 頁面，如果已經打過去後端，就不要再打一次
      if(localStorage.getItem(`${state.oppoId}_updated`)) {
        throw new Error('此交易過去已捐款成功，請勿重複刷新頁面');
      }
      const orderType = rootGetters['donation/orderType'];
      let oppoInfoContent = '';
      let oppoCloseDate;

      switch(orderType) {
        case 'CVS':
          oppoCloseDate = moment(new Date()).add(7, 'days').format('YYYY-MM-DD');
          oppoInfoContent = oneline(`
            <p>請至 7-ELEVEN / 全家 / 萊爾富 / OK超商 多媒體機台列印繳費單繳費</p>
            <ul>
              <li>付款金額 ${ state.ecpayInfo.amount }</li>
              <li>繳費代碼 ${ state.ecpayInfo.paymentNo }</li>
              <li>繳費期限 ${ state.ecpayInfo.expiredTime }</li>
            </ul>
          `);
          break;
        case 'ATM':
          oppoCloseDate = moment(new Date()).add(4, 'days').format('YYYY-MM-DD');
          oppoInfoContent = oneline(`
            <p>請透過 ATM 轉帳至以下銀行帳號以完成繳費（若使用第一銀行 ATM 櫃員機，請選擇「繳費」按鈕）</p>
            <ul>
              <li>付款金額 ${ state.ecpayInfo.amount }</li>
              <li>付款帳號 ${ state.ecpayInfo.paymentAccount }</li>
              <li>繳費期限 ${ state.ecpayInfo.expiredTime }</li>
            </ul>
            <p>跨行轉帳若非使用同銀行 ATM 櫃員機，須支付跨行手續費 15 元，請務必於繳費期限內進行繳費。</p>
          `);
          break;
        case 'CREDIT':
          oppoCloseDate = moment(new Date()).tz('Asia/Taipei').format('YYYY-MM-DD');
          oppoInfoContent = `<p>已用信用卡付款完成</p>`;
      }

      const payload = {
        oppoID: state.oppoId,
        oppoOrderType: orderType,
        oppoCloseDate,
        oppoOrderInfo: oppoInfoContent,
        oppoECPayNo: state.ecpayInfo.oppoECPayNo,
      };

      if(state.ecpayInfo.tradeStatus == '1' | state.ecpayInfo.tradeStatus == '0') {
        // 只有綠界有成功回覆0,1 ，才要打去 SF 更新
        try {
          const result = await updateOpportunityOrderInfo(payload);
          if(result['Fail']) {
            throw new Error(result.msg);
          }
          // 表示此 oppoID 紀錄已經更新到後端
          localStorage.setItem(`${state.oppoId}_updated`, true);
          return result;
        } catch(err) {
          throw new Error(err);
        }

      }
    },
  },
};
